import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Modal } from 'antd';
import CustomButton from '../../../../shared/components/buttons/custom-button/custom-button';
import IconWrapper from '../../../../shared/components/icon-wrapper/icon-wrapper';
import ModalConfirmationContent from '../../../../shared/components/modal-confirmation-content/modal-confirmation-content';
import { useAppSelector } from '../../../../store/hooks';
import { CloseIcon } from '../../../../shared/icons/close-icon';
import LazyModal, { LazyModalComponentProps } from '../../../ui/components/lazy-modal';
import { useGetTemplateFieldsQuery } from '../../../templates/api/templates-api';
import { closeModal } from '../../../ui/store/ui-slice';
import { signRequestsExportModalId } from '../../constants/constants';
import { selectSelectedSignRequestIds } from '../../store/sign-requests-slice';
import { buildSignRequestFiltersPayload } from '../../utils/build-sign-request-filters-payload';
import styles from './sign-requests-export-modal.module.css';

const SignRequestsExportModalContent = ({ isOpen, afterClose }: LazyModalComponentProps): JSX.Element => {
  const { t, i18n } = useTranslation('common');
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const selectedSignRequests = useAppSelector(selectSelectedSignRequestIds);
  const [isDownloading, setIsDownloading] = useState(false);
  const templateCode = searchParams.get('template');
  const { data: templateFieldsData } = useGetTemplateFieldsQuery(templateCode || '', {
    skip: !templateCode,
  });

  const closeFormModal = () => {
    dispatch(closeModal(signRequestsExportModalId));
  };

  const handleExport = async () => {
    setIsDownloading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/portal-api/v2/sign-requests/export`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': i18n.language,
        },
        body: JSON.stringify(
          buildSignRequestFiltersPayload({
            searchParams,
            templateFields: templateFieldsData || [],
            selectedSignRequestIds: selectedSignRequests,
          }),
        ),
      });

      if (!response.ok) {
        throw new Error(`Export failed with status ${response.status}`);
      }

      const blob = await response.blob();
      const filename = response.headers.get('content-disposition')?.match(/filename="?([^"]+)"?/)?.[1];

      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename || 'sign-requests-export';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
      closeFormModal();
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Modal
      className={styles.modal}
      title={t('label.exportModal.title')}
      open={isOpen}
      onCancel={closeFormModal}
      closeIcon={
        <IconWrapper>
          <CloseIcon />
        </IconWrapper>
      }
      afterClose={afterClose}
      footer={[
        <CustomButton type="secondary" key="back" onClick={closeFormModal} className={styles.exportCancel}>
          {t('action.cancel')}
        </CustomButton>,
        <CustomButton type="primary" key="export" loading={isDownloading} onClick={handleExport}>
          {t('action.export')}
        </CustomButton>,
      ]}
    >
      <ModalConfirmationContent confirmDeleteText={t('label.exportModal.warning')} />
    </Modal>
  );
};

const SignRequestsExportModal = (): JSX.Element => {
  return <LazyModal modalId={signRequestsExportModalId} Component={SignRequestsExportModalContent} />;
};

export default SignRequestsExportModal;
